import { BlogCard } from './types';

export const proteinMenopauseBlogCards: BlogCard[] = [
  {
    title: {
      content:
        '### Protein, Muscle And Menopause: Why Maintaining Muscle Matters More Than Ever',
      styles: {
        h3: 'font-Mulish font-bold text-royal-875 my-3 text-base md:text-lg leading-tight line-clamp-2',
      },
      topicsStyles: {
        h3: 'text-theme-primary text-3xl md:text-4xl my-4 md:my-6 p-2 font-black',
      },
    },
    description: {
      content:
        'As women transition through menopause, muscle loss can affect metabolism, balance and long-term health. Learn how protein and resistance training can help preserve muscle mass.',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
      },
    },
    imageUrl: 'herosection-1-resized-2.webp',
    bannerUrl: 'herosection-1-resized-2.webp',
    slug: 'protein-muscle-and-menopause',
    contentType: 'article',
    speciality: 'Nutritional Therapy',
    iconUrl: 'monica-durigon.webp',
    doctorSlug: 'monica-durigon',
    date: {
      content: '01, September 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    postimonial: {
      content: 'PhD, MRCOG, PGCert \n Consultant Obstetrician & Gynaecologist',
      styles: {
        p: 'text-xl whitespace-pre-line',
      },
    },
    author: {
      content: 'Dr Monica Durigon',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    bannerDescription: `Losing muscle during menopause is common, but it is not inevitable. From the age of 40 onwards, muscle mass naturally begins to decline, a process known clinically as sarcopenia. The hormonal shifts of perimenopause, the transitional period before menstruation stops completely, can make this decline feel more pronounced. The good news is that muscle health is something you can actively support through nutrition and exercise. This article explains what happens to muscle during the menopausal transition, how much protein your body actually needs, and why resistance exercise matters just as much as what is on your plate.`,
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'What happens to muscle during menopause?',
          id: 'what-happens-to-your-muscle-during-menopause',
        },
        {
          text: 'Why muscle matters more than you might think',
          id: 'why-muscle-matters-more-than-you-might-think',
        },
        {
          text: 'How much protein do you actually need during menopause?',
          id: 'how-much-protein-do-you-actually-need-during-menopause',
        },
        {
          text: 'How to spread your protein across the day?',
          id: 'how-to-spread-your-protein-across-the-day',
        },
        {
          text: 'Protein alone is not enough: why exercise is the other half of the equation',
          id: 'proteins-alone-is-not-enough',
        },
        {
          text: 'Frequently asked questions about protein, muscle and menopause',
          id: 'frequently-asked-questions',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    content: {
      content: `

<span id="what-happens-to-your-muscle-during-menopause"></span>

### What happens to muscle during menopause?

Oestrogen has wide-ranging effects throughout the body, and skeletal muscle is no exception. During the menopausal transition, falling oestrogen levels occur alongside age-related changes in muscle protein turnover, physical activity and body composition . Research suggests that lean muscle mass can decline meaningfully across this period, and the age-related loss of muscle mass and strength, a condition called sarcopenia, may progress more rapidly without deliberate intervention.

<div align="center">\n\n
![what happens to your muscle during menopause](what-happens-to-your-muscle-during-menopause-resized.webp "600x400")\n\n
</div>

Many women notice changes in body composition during midlife even without significant changes in overall body weight. Fat tends to accumulate more readily around the abdomen, while holding on to muscle becomes more challenging. This body composition shift carries real metabolic consequences that go beyond how we look or feel day to day.

This is one reason why focusing on body weight alone can be misleading. Two women can weigh exactly the same and have very different proportions of muscle and body fat, and therefore very different long-term health profiles. If you are noticing these changes and you live in Chelsea, Clapham, Vauxhall, Pimlico or Wandsworth, a consultation at a specialist menopause clinic in London can help you understand what is happening in your body and what to do about it.

<span id="why-muscle-matters-more-than-you-might-thin"></span>

### Why muscle matters more than you might think?

Muscle is one of the most metabolically important tissues in the body. It is far more than a measure of how strong or toned you look. Adequate muscle mass directly supports:

<div style="display: flex; justify-content: space-between; gap: 40px; text-align: left;">

  <div style="flex: 1;">
    <ul>
      <li>Physical strength and ease of everyday movement</li>
      <li>Healthy blood glucose regulation</li>
      <li>Metabolic function and energy use throughout the day</li>
    </ul>
  </div>

  <div style="flex: 1;">
    <ul>
      <li>Bone and joint health, reducing the risk of osteoporosis</li>
      <li>Balance and fall prevention later in life</li>
      <li>Independence and quality of life as you age</li>
    </ul>
  </div>
</div>

<div align="center">\n\n
![why muscle matters more than you might think](why-muscle-matters-more-than-you-might-think.webp "600x400")\n\n
</div>

Muscle also plays a central role in blood sugar control. After eating, muscle tissue helps remove glucose from the bloodstream and store or use it as energy. This makes preserving muscle particularly relevant in midlife, when insulin sensitivity and fat distribution may already be shifting in ways that increase metabolic risk.

If you are approaching or going through menopause and noticing changes in your strength, energy or body composition, speaking to a nutritional therapy practitioner at LIPS Healthcare, I can help you build a clear, practical plan.

<div style="text-align:center;margin:2rem 0;">
<strong><a href="/our-specialists/monica-durigon" target="_blank" rel="noopener noreferrer">Talk to a nutritionist about menopause</a></strong></div>

<span id="how-much-protein-do-you-actually-need-during-menopause"></span>

### How much protein do you actually need during menopause?

The standard UK recommendation for dietary protein in adults is approximately 0.75 g per kilogram of body weight per day. However, research into healthy ageing increasingly suggests that this figure may not be sufficient to support muscle health as we get older.

<div align="center">\n\n
![How much protein do you actually need during menopause](how-much-protein-do-you-actually-need-during-menopause.webp "600x400")\n\n
</div>

Most current evidence for healthy older adults, including those going through the menopausal transition, points to approximately 1.0 to 1.2 g of protein per kilogram of body weight per day. For some individuals, particularly those undertaking regular resistance training, recovering from illness or at greater risk of muscle loss, even higher intakes may be appropriate.

#### Practical example
A woman weighing 65 kg aiming for 1.0 to 1.2 g per kilogram of body weight would need approximately 65 to 78 g of protein per day.

| Weight (kg) | Protein per day |
|---|---|
| 65 kg | 65 - 78 g |

Protein needs are highly individual, however. Body size, activity levels, overall energy intake, health status and personal goals all influence requirements. Higher-protein diets are not appropriate for everyone, particularly in the presence of certain medical conditions. As a Nutritional Therapy practitioner, I can help you calculate a daily target that fits your body, your routine, and your health goals.

<span id="how-to-spread-your-protein-across-the-day"></span>

### How to spread your protein across the day?

Spreading protein evenly across breakfast, lunch and dinner supports muscle protein synthesis more effectively than concentrating most of it in a single meal. Muscle protein synthesis is the process by which the body repairs and rebuilds muscle tissue, and it responds to repeated protein signals across the day rather than one large intake.

<div align="center">\n\n
![How to spread your protein across the day](how-to-spread-your-protein-across-the-day.webp "600x400")\n\n
</div>

A common pattern is to eat very little protein at breakfast, a moderate amount at lunch and the majority at dinner. Research on healthy ageing suggests that redistributing protein more evenly across all three meals may help maximise this effect.

<div align="center">\n\n
#### A practical approach is to include a meaningful protein source at each main meal. Some examples:\n\n
</div>

<div align="center">\n\n
![how to spread your protein across the day content](how-to-spread-your-protein-across-the-day-content-resized.webp "600x400")\n\n
</div>

Plant proteins count equally. The priority is obtaining enough total protein across the day while maintaining a varied, fibre-rich diet.

<span id="proteins-alone-is-not-enough"></span>

### Protein alone is not enough: why exercise is the other half of the equation

Eating more protein will not automatically build or preserve muscle. Muscle needs a physical reason to adapt, and that stimulus comes primarily from resistance exercise. Strength training challenges muscle tissue and triggers the physiological adaptation process. Dietary protein then provides the amino acids needed for repair and remodelling. Neither works as well without the other.

<div align="center">\n\n
![proteins alone is not enough](proteins-alone-is-not-enough-resized.webp "600x400")\n\n
</div>

Research on this point is consistent: protein supplementation alone does not reliably increase muscle mass or strength in healthy older adults . The benefit becomes meaningful when adequate protein intake is combined with regular resistance training. Studies specifically involving women across different life stages, including around the menopausal transition, confirm that exercise provides reliable benefits for musculoskeletal health.

#### Resistance exercise options include:
- Free weights
- Resistance machines
- Resistance bands
- Body-weight exercises such as squats, lunges and press-ups

The key factor is progressive challenge: as strength improves, the load or resistance should increase to continue stimulating adaptation. Current UK physical activity guidance from the Department of Health and Social Care recommends muscle-strengthening activity on at least two days per week , though many women working with a Nutritional Therapy practitioner or physiotherapist benefit from doing more.

LIPS Healthcare offers nutrition consultations and access to a rehabilitation gym at Battersea Power Station, London, making it straightforward to address both protein intake and exercise as part of a single, joined-up plan. Patients travel to us from Chelsea, Clapham, Nine Elms, Pimlico, Vauxhall and Wandsworth, and we are a short walk from Battersea Power Station station on the Northern line.

<span id="frequently-asked-questions"></span>
`,
      styles: {
        h3: 'text-lg md:text-[32px] font-bold text-theme-primary mb-4',
        h4: 'text-lg md:text-[20px] font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action',
        em: 'not-italic font-bold',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        table:
          'w-full table-fixed border-collapse text-xs md:text-sm border-2 border-black',
        th: 'border-2 border-black bg-text-muted-blue px-2 py-2 md:px-4 md:py-3 text-left font-semibold text-theme-primary whitespace-normal wrap-break-word',
        td: 'border-2 border-black px-2 py-2 md:px-4 md:py-3 align-top font-semibold whitespace-normal wrap-break-word',
      },
      tableOptions: {
        width: '100%',
        columnWidths: ['50%', '50%'],
      },
    },
    faq: {
      heading: {
        content: '#### Frequently asked questions (FAQS)',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content: 'Does menopause cause muscle loss?',
          },
          answer: {
            content:
              'Yes, in part. From the age of 40 onwards, muscle mass naturally begins to decline as part of ageing, a condition known as sarcopenia. Menopause may accelerate this process as oestrogen levels fall. Research is still establishing how much of the change is specific to menopause and how much is due to ageing more broadly [1]. What is clear is that muscle loss during this period is not inevitable and can be meaningfully slowed with the right combination of nutrition and resistance exercise.',
          },
        },
        {
          question: {
            content:
              'How much protein should a woman in menopause eat each day?',
          },
          answer: {
            content:
              'Approximately 1.0 to 1.2 g of protein per kilogram of body weight per day, based on current evidence for healthy ageing populations. For a woman weighing 65 kg, that equates to roughly 65 to 78 g of protein daily. Individual needs vary based on activity level, health status, and other factors. As a Nutritional Therapy practitioner at LIPS Healthcare, I can help you set a personalised daily target.',
          },
        },
        {
          question: {
            content:
              'Do protein supplements help with muscle loss during menopause?',
          },
          answer: {
            content:
              'Supplements can help some women reach an adequate daily intake, particularly where appetite is reduced, or dietary patterns make it difficult to get enough protein from food alone. However, protein supplementation alone does not consistently increase muscle mass or strength in healthy older adults. The research most consistently supports the combination of adequate protein and regular resistance training, not supplementation on its own.',
          },
        },
        {
          question: {
            content:
              'Can I lose weight and protect my muscle at the same time during menopause?',
          },
          answer: {
            content:
              'Yes, with the right approach. Aggressive calorie restriction tends to increase the loss of lean tissue alongside body fat. A more effective strategy is a moderate calorie deficit combined with adequate protein intake and regular resistance training. Working with a nutritionist ensures that any weight loss plan is structured to preserve as much muscle as possible.',
          },
        },
        {
          question: {
            content: 'What should I focus on first: protein or exercise?',
          },
          answer: {
            content:
              'Both. Neither works as well without the other, but if you are new to resistance training, starting there while gradually improving your protein intake is a sensible approach. If you already exercise regularly, reviewing your daily protein distribution is a logical next step. A consultation at a specialist menopause clinic in London, such as LIPS Healthcare at Battersea Power Station, can help you prioritise based on where you are right now.',
          },
        },
        {
          question: {
            content: 'Is it worth seeing a nutritionist about menopause?',
          },
          answer: {
            content:
              'Yes, particularly if you are noticing changes in body composition, energy or strength that are not responding to your current diet and exercise habits. As a Nutritional Therapy practitioner at LIPS Healthcare, I can review your dietary intake, identify gaps, and build a practical, evidence-based plan tailored to your needs. We see patients from across south and central London, including Chelsea, Clapham, Vauxhall, Wandsworth and Pimlico.',
          },
        },
      ],
    },
  },
];
